////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.3                                                            //
//  Summary: Projects listing route — GET /public/projects, honestly empty  //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import { motion } from 'framer-motion';

import { useApi } from '@/hooks/useApi';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { cardHover, reduceHover } from '@/lib/motion';
import type { ProjectListResponse } from '@/types/public';
import { Spinner } from '@/components/feedback/Spinner';
import { ErrorState } from '@/components/feedback/ErrorState';
import { EmptyState } from '@/components/feedback/EmptyState';
import { Select } from '@/components/forms/Select';
import { PageHero } from '@/components/content/PageHero';
import { Reveal, RevealGroup, RevealItem } from '@/components/content/Reveal';

const REQUEST_URL = '/public/projects?limit=24&page=1';

export function Projects () {
  usePageTitle('Projects');

  const reducedMotion = useReducedMotion();
  const { status, data } = useApi<ProjectListResponse>(REQUEST_URL);

  return (
    <div>
      <PageHero
        title="Projects"
        subtitle="The pursuit of excellence across every project. The culmination of our steadfast processes are these case studies."
      />

      <div className="mx-auto max-w-6xl px-6 py-16">
        {/* content.projects is empty until marketing populates it (1.0.2.md §2.4) — the
            filter controls are real, wired to the eventual query params, but disabled while
            there is nothing to filter. */}
        <Reveal as="div" className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <Select id="filter-type" label="Type" options={[]} value={null} onChange={() => {}} disabled placeholder="All types" />
          <Select id="filter-status" label="Status" options={[]} value={null} onChange={() => {}} disabled placeholder="All statuses" />
          <Select id="filter-location" label="Location" options={[]} value={null} onChange={() => {}} disabled placeholder="All locations" />
          <Select id="filter-completion" label="Completion" options={[]} value={null} onChange={() => {}} disabled placeholder="Any" />
        </Reveal>

        {status === 'loading' && <div className="mt-10"><Spinner label="Loading projects" /></div>}

        {status === 'error' && (
          <div className="mt-10">
            <ErrorState message="Projects couldn't be loaded right now." />
          </div>
        )}

        {status === 'success' && data.items.length === 0 && (
          <div className="mt-10">
            <EmptyState message="Project case studies are being updated." />
          </div>
        )}

        {status === 'success' && data.items.length > 0 && (
          <RevealGroup as="ul" className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.items.map((project) => (
              <RevealItem key={project.slug} as="li">
                {/* `.glass-card`, not <GlassTile> — see styles/glass.css: one
                    backdrop-filter per card across a grid is the stacking
                    foot-gun §5's perf guardrail forbids. */}
                <motion.div whileHover={reduceHover(cardHover, reducedMotion)}>
                  <div className="glass-card p-4">
                    <p className="font-medium text-ink">{project.title}</p>
                    <p className="text-sm text-muted">{project.location}</p>
                  </div>
                </motion.div>
              </RevealItem>
            ))}
          </RevealGroup>
        )}
      </div>
    </div>
  );
}
